import { cn } from "@volca/ui/lib/utils";
import { points } from "@volca/ui/trades";
import { moneyChange } from "@/profitability/currency";
import { MetaList } from "@/profitability/meta-list";
import { MissingFigure, MissingValue } from "@/profitability/missing-value";
import type { ReportColumn } from "@/profitability/types";
import { formatSummaryValue } from "@/profitability/utils";
import { formatRatio, type Variance } from "./estimates";

/** A change in the actual column's own terms: "+$460", "+2.0 pts", "+3". */
const formatChange = (
  change: number,
  column: ReportColumn,
  currency: string,
) => {
  if (column.type === "currency") return moneyChange(change, currency);
  if (column.type === "percent") return points(change);
  return `${change < 0 ? "−" : "+"}${Math.abs(change).toLocaleString()}`;
};

/** "+$460 (+12.3%)", in the colour of whether that is good news. */
export const VarianceText = ({
  variance,
  column,
  currency,
  className,
}: {
  variance: Variance;
  column: ReportColumn;
  currency: string;
  className?: string;
}) => {
  if (variance.change === null) return null;
  const ratio =
    variance.ratio !== null && column.type !== "percent"
      ? ` (${formatRatio(variance.ratio)})`
      : "";
  return (
    <span
      className={cn(
        "tabular-nums",
        {
          "text-success": variance.tone === "good",
          "text-danger": variance.tone === "bad",
          "text-subtle": variance.tone === "neutral",
        },
        className,
      )}
    >
      {formatChange(variance.change, column, currency)}
      {ratio}
    </span>
  );
};

/**
 * An actual against its estimate, for a record: "$4,210 vs $3,750 est.",
 * a separator, then "+$460 (+12.3%)". Either side may be missing.
 */
export const ActualVsEstimate = ({
  variance,
  column,
  currency,
}: {
  variance: Variance;
  column: ReportColumn;
  currency: string;
}) => {
  const value = (number: number | null) =>
    number === null ? (
      <MissingValue />
    ) : (
      formatSummaryValue({ value: number, type: column.type }, currency)
    );
  return (
    <MetaList className="tabular-nums">
      <span className="inline-flex flex-wrap items-baseline gap-x-1.5">
        {/* The actual not in yet is a gap in the files; an estimate left
        blank is only a job without one. */}
        <span>
          {variance.actual === null ? (
            <MissingFigure source={column.source ?? null} />
          ) : (
            value(variance.actual)
          )}
        </span>
        <span className="text-sm font-normal text-subtle">
          vs {value(variance.estimate)} est.
        </span>
      </span>
      {variance.change !== null && (
        <VarianceText
          variance={variance}
          column={column}
          currency={currency}
          className="text-sm font-normal"
        />
      )}
    </MetaList>
  );
};

/**
 * The variance as a quiet second line under a table cell's actual, the
 * estimate itself in its tooltip.
 */
export const VarianceLine = ({
  variance,
  column,
  currency,
}: {
  variance: Variance;
  column: ReportColumn;
  currency: string;
}) => {
  if (variance.change === null) return null;
  const estimate =
    variance.estimate === null
      ? ""
      : formatSummaryValue(
          { value: variance.estimate, type: column.type },
          currency,
        );
  return (
    <span title={`Estimate ${estimate}`} className="block text-xs font-normal">
      <VarianceText variance={variance} column={column} currency={currency} />
      <span className="text-subtle"> vs est.</span>
    </span>
  );
};
